const en = {
  modal: {
    eyebrow: 'Language',
    title: 'Choose your language',
    subtitle: 'Select the language to view the site.',
    helper: 'Spanish / English / Portuguese'
  },
  nav: {
    home: 'Home',
    services: 'Services',
    solutions: 'Solutions',
    stack: 'Stack',
    process: 'Process',
    team: 'Our team',
    press: 'Press',
    contact: 'Contact',
    menu: 'Menu',
    language: 'Language',
    cta: 'Book a meeting'
  },
  hero: {
    badge: 'Software development + Infrastructure + Automation',
    title: 'Software development that connects systems, networks, and industrial processes.',
    description:
      'At Genius Software, we are a software development company focused on web development, mobile applications, and systems integration. We work with React, Python, and modern stacks to improve operational efficiency, security, and real-time visibility.',
    actions: {
      primary: 'Schedule a meeting',
      secondary: 'View services',
      whatsapp: 'Contact via WhatsApp'
    },
    metrics: [
      { value: '+20', label: 'active projects' },
      { value: '99.9%', label: 'infrastructure uptime' },
      { value: '48h', label: 'average delivery time' }
    ],
    highlights: [
      {
        title: 'Custom software',
        text: 'Web development and apps tailored to your needs.'
      },
      {
        title: 'Secure IT infrastructure',
        text: 'Networks, servers, and continuity.'
      },
      {
        title: 'Industrial automation',
        text: 'Control, traceability, and data.'
      }
    ],
    card: {
      title: 'End-to-end solutions',
      text: 'Software, networks, and industrial automation in one team.',
      chips: ['React', 'Python', 'DevOps', 'SCADA']
    },
    hud: {
      latencyLabel: 'Latency',
      availabilityLabel: 'Availability',
      nodesLabel: 'Nodes'
    }
  },
  sections: {
    services: {
      eyebrow: 'Services',
      title: 'Software development, IT infrastructure, and industrial automation solutions.',
      subtitle:
        'We design each proposal around your goals, integrating custom software development, web development, IT infrastructure, and process automation.'
    },
    company: {
      eyebrow: 'Our company',
      title: 'Technology focused on results.',
      subtitle:
        'We are a software development and technology consulting company in Rosario, Argentina. We deliver tailored solutions to optimize processes, improve operational efficiency, and support the growth of companies in a digital environment.'
    },
    process: {
      eyebrow: 'Process',
      title: 'A clear flow for successful projects.',
      subtitle: 'Transparency, collaboration, and continuous improvement at every stage.'
    },
    stack: {
      eyebrow: 'Tech stack',
      title: 'Modern, scalable technologies.',
      subtitle:
        'We combine full-stack development, web development, cloud, and Industry 4.0 to create solid solutions ready to grow.',
      note: 'React, Python, APIs, databases, DevOps, and IT infrastructure in a single technical roadmap.'
    },
    industries: {
      eyebrow: 'Industries',
      title: 'Experience across multiple industries.',
      subtitle:
        'We adapt to each operation to ensure results aligned with your strategic objectives.'
    },
    team: {
      eyebrow: 'Trust that keeps growing',
      metric: '50+',
      title: 'companies trust our solutions.',
      subtitle: 'Technology designed to solve real challenges and support every company as it grows.'
    },
    teamMembers: {
      eyebrow: 'Our team',
      title: 'The people behind every solution.',
      subtitle:
        'A multidisciplinary team specializing in software, infrastructure, business, and automation.'
    },
    contact: {
      eyebrow: 'Contact',
      title: 'Take your business to the next level.',
      subtitle:
        'Schedule a meeting and let us help your business grow with custom software development solutions.'
    }
  },
  services: [
    {
      title: 'Custom software',
      description: 'Software development, web and mobile applications aligned with your operations.',
      points: [
        'Web development and mobile applications',
        'Modern, accessible UX/UI',
        'Secure integrations and APIs',
        'Dashboards and analytics'
      ],
      icon: 'software'
    },
    {
      title: 'Infrastructure and networks',
      description: 'IT infrastructure, architecture, security, and reliable connectivity.',
      points: [
        'Network design and implementation',
        'Server and service administration',
        'Continuous monitoring and support'
      ],
      icon: 'infra'
    },
    {
      title: 'Industrial automation',
      description: 'Industry 4.0 with more efficient processes and real-time data.',
      points: [
        'Sensor and PLC integration',
        'SCADA, dashboards, and alerts',
        'Traceability and control'
      ],
      icon: 'automation'
    },
    {
      title: 'Cloud and continuity',
      description: 'Cloud services and business continuity ready to scale.',
      points: [
        'Cloud migrations and managed hosting',
        'Backups, DRP, and high availability',
        'DevOps and pipelines'
      ],
      icon: 'cloud'
    }
  ],
  differentiators: [
    {
      title: 'Senior team',
      description: 'Professionals in software, infrastructure, and automation.'
    },
    {
      title: 'Agile methodology',
      description: 'Short sprints, full visibility, and iterative deliveries.'
    },
    {
      title: 'Security from day one',
      description: 'Best practices, access control, and proactive monitoring.'
    }
  ],
  companyCard: {
    title: 'Service model',
    description: 'Clear communication and close project tracking with a focus on measurable results.',
    checklist: [
      'Discovery and diagnosis',
      'Roadmap with iterative deliveries',
      'Documentation and training',
      'Support and continuous improvement'
    ]
  },
  companyStrip: {
    locationLabel: 'Location',
    locationValue: 'Rosario, Argentina',
    contactLabel: 'Contact',
    contactValue: '3416926584'
  },
  process: [
    {
      step: '01',
      title: 'Discovery',
      description: 'We map processes, objectives, and constraints.',
      icon: 'discover'
    },
    {
      step: '02',
      title: 'Design',
      description: 'Architecture, UX/UI, and technical roadmap.',
      icon: 'design'
    },
    {
      step: '03',
      title: 'Build',
      description: 'Development, infrastructure, and automation.',
      icon: 'build'
    },
    {
      step: '04',
      title: 'Evolution',
      description: 'Support, improvements, and new integrations.',
      icon: 'evolve'
    }
  ],
  stack: {
    items: [
      'React',
      'Python',
      'Node.js',
      'Django',
      'FastAPI',
      'PostgreSQL',
      'SQL Server',
      'Docker',
      'Kubernetes',
      'AWS',
      'Azure',
      'Grafana',
      'SCADA',
      'PLC'
    ],
    note: 'React, Python, APIs, databases, DevOps, and IT infrastructure in a single technical roadmap.'
  },
  industries: [
    {
      title: 'Manufacturing',
      description: 'Production lines, maintenance, and traceability.',
      icon: 'manufacturing'
    },
    {
      title: 'Agribusiness and food',
      description: 'Production control, inventory, and quality.',
      icon: 'agro'
    },
    {
      title: 'Logistics',
      description: 'Monitoring, fleets, and route optimization.',
      icon: 'logistics'
    },
    {
      title: 'Energy',
      description: 'Telemetry, remote operations, and safety.',
      icon: 'energy'
    },
    {
      title: 'Healthcare',
      description: 'Integrations, reports, and critical processes.',
      icon: 'health'
    },
    {
      title: 'Services',
      description: 'Digital platforms for clients and teams.',
      icon: 'services'
    }
  ],
  team: [
    {
      name: 'Luciano Rodas',
      role: 'Solutions Architect',
      initials: 'LR',
      photo: `${process.env.PUBLIC_URL}/equipo/lucho.png`
    },
    {
      name: 'Joel Mansilla',
      role: 'Backend Developer',
      initials: 'JM',
      photo: `${process.env.PUBLIC_URL}/equipo/joel.png`
    },
    {
      name: 'Mauricio Arroyo',
      role: 'Full-Stack Developer',
      initials: 'MA',
      photo: `${process.env.PUBLIC_URL}/equipo/mauri.png`
    },
    {
      name: 'Mauro Bertolone',
      role: 'Full-Stack Developer',
      initials: 'MB',
      photo: `${process.env.PUBLIC_URL}/mauro.jpeg`,
      photoPosition: '50% 31%',
      photoScale: 2.6
    },
    {
      name: 'Clara Pereyra',
      role: 'Application Support',
      initials: 'CP',
      photo: `${process.env.PUBLIC_URL}/clara.jpeg`,
      photoPosition: '50% 38%'
    },
    {
      name: 'Delfina Waters',
      role: 'Marketing and Communications',
      initials: 'DW',
      photo: `${process.env.PUBLIC_URL}/delfi.jpeg`,
      photoPosition: '50% 15%',
      photoScale: 1.5
    },
    {
      name: 'Brenda Avalos',
      role: 'Sales Representative',
      initials: 'BA',
      photo: `${process.env.PUBLIC_URL}/brenda.jpeg`,
      photoPosition: '50% 40%',
      photoScale: 1.6
    },
    {
      name: 'Pablo Fratini',
      role: 'Team Leader',
      initials: 'PF',
      photo: `${process.env.PUBLIC_URL}/equipo/pablo.png`
    }
  ],
  press: {
    hero: {
      eyebrow: 'Press',
      title: 'Articles and interviews about Genius Software.',
      subtitle:
        'A curated set of publications showing how we bring custom software, automation, and real digital transformation to industrial operations.',
      primaryAction: 'Go to contact',
      secondaryAction: 'Back to home',
      featuredLabel: 'Featured article',
      tags: ['Desarrollo Zonal', 'Punto Biz', 'Industry 4.0']
    },
    section: {
      eyebrow: 'Coverage',
      title: 'Media outlets that told our story.',
      subtitle:
        'We gathered the articles published about the growth of Genius Software, its industrial roots, and its focus on practical production processes.'
    },
    cardButton: 'Read article',
    videoFallback: 'Your browser cannot play this video.',
    cta: {
      eyebrow: 'Next step',
      title: 'Want to see what we are building?',
      subtitle:
        'Go back to the main site to explore our services or schedule a meeting to bring these solutions into your company.',
      primaryAction: 'Talk to Genius',
      secondaryAction: 'View main site'
    },
    articles: [
      {
        outlet: 'Desarrollo Zonal',
        category: 'Science & Tech',
        date: 'March 21, 2026',
        title: 'La industria se transforma: dos emprendedores llevan la digitalizacion a las fabricas',
        description:
          'Desarrollo Zonal presented Genius Software as a regional company transforming production processes with custom software and industrial automation.',
        image: `${process.env.PUBLIC_URL}/dzprensa.png`,
        imageAlt: 'Screenshot of the Desarrollo Zonal article about Genius Software',
        href: 'https://desarrollozonal.com/2026/03/21/la-industria-se-transforma-dos-emprendedores-llevan-la-digitalizacion-a-las-fabricas/',
        hostLabel: 'desarrollozonal.com'
      },
      {
        outlet: 'Punto Biz',
        category: 'Current Affairs',
        date: 'March 5, 2026',
        title: 'De una metalurgica local a digitalizar la industria nacional',
        description:
          'Punto Biz traced the company origin, from a hands-on metalworking experience to a scalable proposal for digitalizing industrial operations.',
        image: `${process.env.PUBLIC_URL}/puntobizprensa.png`,
        imageAlt: 'Screenshot of the Punto Biz article about Genius Software',
        href: 'https://puntobiz.com.ar/actualidad/de-una-metalurgica-local-a-digitalizar-la-industria-nacional-202635600',
        hostLabel: 'puntobiz.com.ar'
      },
      {
        outlet: 'ONE VISION',
        category: 'Social project',
        title: 'ONE VISION social project',
        description:
          'An initiative that brings together technology, commitment, and community to create a positive social impact.',
        video: `${process.env.PUBLIC_URL}/santafe.mp4`,
        videoLabel: 'ONE VISION social project video',
        hostLabel: 'Video content'
      }
    ]
  },
  contact: {
    actions: {
      email: 'Send email',
      call: 'Call now'
    },
    form: {
      title: 'Send us a message',
      subtitle: 'Tell us about your project and we will get back to you.',
      nameLabel: 'Name',
      phoneLabel: 'Phone',
      emailLabel: 'Email',
      messageLabel: 'Message',
      namePlaceholder: 'Your name',
      phonePlaceholder: 'Your phone',
      emailPlaceholder: 'Your email',
      messagePlaceholder: 'Tell us what you need',
      mailSubject: 'New website inquiry -',
      submit: 'Send message'
    },
    widget: {
      button: 'Messages',
      openLabel: 'Open contact form',
      closeLabel: 'Close contact form',
      assistant: {
        title: 'Genius Assistant',
        subtitle: 'I am available. Ask me anything.'
      }
    },
    card: {
      webLabel: 'Web',
      emailLabel: 'Email',
      phoneLabel: 'Phone',
      locationLabel: 'Location'
    },
    values: {
      web: 'www.sistemasgenius.com',
      email: 'comercial@sistemasgenius.com',
      phone: '3416926584',
      location: 'Córdoba 836, Rosario, Santa Fe, Argentina'
    }
  },
  footer: {
    brand: 'Genius Software',
    fiscalTitle: 'Tax information',
    legalLines: ['Genius Software S.A.S.', 'CUIT 30-71926918-0'],
    mapTitle: 'Genius Software location at Córdoba 836, Rosario',
    mapsLabel: 'View our Google Maps profile',
    directionsLabel: 'Get directions',
    description:
      'End-to-end custom software development, web development, IT infrastructure, and industrial automation solutions.',
    servicesTitle: 'Services',
    servicesItems: [
      'Custom software',
      'Infrastructure and networks',
      'Industrial automation',
      'Cloud and continuity'
    ],
    contactTitle: 'Contact',
    contactItems: ['comercial@sistemasgenius.com', '3416926584']
  },
  whatsapp: {
    label: 'Open WhatsApp for inquiries',
    text: 'We are available, message us.'
  },
  images: {
    heroPrimaryAlt: 'Primary image',
    heroSecondaryAlt: 'Secondary image',
    splitPrimaryAlt: 'Team image',
    splitSecondaryAlt: 'Office image',
    industriesPrimaryAlt: 'Industry image',
    industriesSecondaryAlt: 'Technology image'
  }
};

export default en;

